import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, Loader, RefreshCw, Zap, Play, Music, MessageSquare } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import ImageModal from './ImageModal';
import { playSuccessSound } from '../utils/soundUtils';
import { normalizeStaticUrl } from '../utils/urlUtils';
import './JobHistory.css';

// Вспомогательная функция для определения типа медиа
const isVideoFile = (url) => {
  if (!url) return false;
  const urlLower = url.toLowerCase();
  return urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.webm') || urlLower.includes('.gif');
};

function formatChatTokens(value) {
  const n = Number(value) || 0;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/\.?0+$/, '');
}

const isAudioFile = (url) => {
  if (!url) return false;
  const path = url.toLowerCase().split('?')[0];
  return ['.mp3', '.wav', '.m4a', '.ogg', '.aac', '.flac'].some((ext) => path.endsWith(ext));
};

// Функция для получения URL миниатюры
const getThumbnailUrl = (originalUrl) => {
  if (!originalUrl) return null;
  
  // Проверяем, является ли URL локальным (на нашем сервере)
  const isLocalUrl = originalUrl.includes('/static/results/') || 
                     originalUrl.includes('pikly.ru/static/results/') ||
                     originalUrl.startsWith('/static/results/');
  
  if (!isLocalUrl) {
    // Для внешних URL (Replicate) миниатюры нет, возвращаем оригинал
    return null;
  }
  
  // Извлекаем имя файла из URL
  const urlParts = originalUrl.split('/');
  const filename = urlParts[urlParts.length - 1];
  
  // Убираем расширение и добавляем _thumb.jpg
  const nameWithoutExt = filename.split('.')[0];
  const thumbnailFilename = `${nameWithoutExt}_thumb.jpg`;
  
  // Строим путь к миниатюре
  // Заменяем /results/filename.ext на /results/thumbnails/filename_thumb.jpg
  let thumbnailsPath;
  if (originalUrl.includes('/static/results/')) {
    thumbnailsPath = originalUrl.replace('/static/results/', '/static/results/thumbnails/').replace(filename, thumbnailFilename);
  } else {
    // Fallback для других форматов URL
    const basePath = originalUrl.substring(0, originalUrl.lastIndexOf('/'));
    thumbnailsPath = `${basePath}/thumbnails/${thumbnailFilename}`;
  }
  
  return thumbnailsPath;
};

function JobHistory({ jobs, loading: initialLoading = false, onRefresh, onLoadMore }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all, completed, processing, failed, pending
  const [hasMore, setHasMore] = useState(true); // Есть ли еще записи для загрузки
  const [statusUpdateTrigger, setStatusUpdateTrigger] = useState(0); // Триггер для перерисовки при обновлении статусов
  const [presets, setPresets] = useState({}); // Хранилище пресетов по preset_id
  const [chatOps, setChatOps] = useState([]);
  const jobsRef = React.useRef(jobs);
  const isUpdatingRef = React.useRef(false); // Для предотвращения одновременных обновлений статусов
  const statusUpdatesRef = React.useRef({}); // Локальное хранилище обновлений статусов

  // Загружаем пресеты для задач, у которых есть preset_id
  // Загружаются только пресеты из текущего списка задач (видимых пользователю)
  // Это эффективно: если много задач - они обычно подгружаются по частям (пагинация)
  useEffect(() => {
    const loadPresets = async () => {
      // Собираем уникальные preset_id только из текущего списка задач (который видит пользователь)
      // Обычно это ограниченное количество благодаря пагинации или ограничению на загрузку
      const presetIds = [...new Set(jobs
        .filter(job => job.preset_id) // Только задачи с preset_id
        .map(job => job.preset_id)
      )];
      
      console.log('JobHistory: Найдено preset_id:', presetIds);
      
      if (presetIds.length === 0) return;
      
      // Используем функциональное обновление для проверки уже загруженных пресетов
      setPresets(prevPresets => {
        // Фильтруем только те, которые еще не загружены
        const missingPresetIds = presetIds.filter(id => !prevPresets[id]);
        
        if (missingPresetIds.length === 0) {
          return prevPresets; // Все пресеты уже загружены
        }
        
        // Загружаем недостающие пресеты параллельно
        (async () => {
          try {
            const presetPromises = missingPresetIds.map(presetId =>
              axios.get(`/api/presets/${presetId}`).catch(() => null)
            );
            
            const presetResponses = await Promise.all(presetPromises);
            
            // Обновляем состояние пресетов функциональным обновлением
            setPresets(currentPresets => {
              const updatedPresets = { ...currentPresets };
              presetResponses.forEach((response, index) => {
                if (response && response.data) {
                  const presetId = missingPresetIds[index];
                  // Проверяем еще раз на случай параллельных запросов
                  if (!updatedPresets[presetId]) {
                    updatedPresets[presetId] = {
                      title: response.data.title,
                      preset_id: response.data.preset_id
                    };
                    console.log(`Загружен пресет: ${presetId} -> ${response.data.title}`);
                  }
                }
              });
              return updatedPresets;
            });
          } catch (error) {
            console.error('Error loading presets:', error);
          }
        })();
        
        // Возвращаем текущее состояние без изменений (обновление произойдет асинхронно)
        return prevPresets;
      });
    };
    
    loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  const loadChatOps = async () => {
    try {
      const response = await axios.get('/api/chat/operations', { params: { limit: 100 } });
      setChatOps(Array.isArray(response.data) ? response.data : []);
    } catch {
      setChatOps([]);
    }
  };

  useEffect(() => {
    loadChatOps();
  }, [location.pathname]);

  // Фильтруем и сортируем задачи
  // Применяем обновления статусов из локального хранилища
  const filteredJobs = React.useMemo(() => {
    // Читаем statusUpdateTrigger для триггера пересчета при обновлении статусов
    // (значение не используется напрямую, но нужно для пересчета useMemo)
    void statusUpdateTrigger;
    
    let filtered = [...jobs];
    
    // Применяем обновления статусов из локального хранилища
    filtered = filtered.map(job => {
      const update = statusUpdatesRef.current[job.public_id];
      if (update) {
        return {
          ...job,
          status: update.status,
          output_image_url: update.output_image_url || job.output_image_url,
          error_message: update.error_message || job.error_message,
          completed_at: update.completed_at || job.completed_at
        };
      }
      return job;
    });
    
    // Применяем фильтр по статусу
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }
    
    const chatRows = (statusFilter === 'all' || statusFilter === 'completed' ? chatOps : []).map((op) => ({
      kind: 'chat',
      id: `chat-${op.id}`,
      status: 'completed',
      created_at: op.created_at,
      public_id: `chat-${op.id}`,
      model_name: op.model_name,
      title: op.title,
      billed_tokens: op.billed_tokens,
      prompt_tokens: op.prompt_tokens,
      completion_tokens: op.completion_tokens,
    }));

    // Сортируем по дате (новые сначала)
    return [...filtered, ...chatRows].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [jobs, chatOps, statusFilter, statusUpdateTrigger]);

  // Подсчет статусов
  const statusCounts = React.useMemo(() => {
    return {
      all: jobs.length + chatOps.length,
      completed: jobs.filter(j => j.status === 'completed').length + chatOps.length,
      processing: jobs.filter(j => j.status === 'processing').length,
      failed: jobs.filter(j => j.status === 'failed').length,
      pending: jobs.filter(j => j.status === 'pending').length,
    };
  }, [jobs, chatOps]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="status-icon completed" />;
      case 'failed':
        return <XCircle className="status-icon failed" />;
      case 'processing':
        return <Loader className="status-icon processing animate-spin" />;
      default:
        return <Clock className="status-icon pending" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Завершено';
      case 'failed':
        return 'Ошибка';
      case 'processing':
        return 'Обработка';
      default:
        return 'Ожидание';
    }
  };

  const handleRefresh = async () => {
    if (!onRefreshRef.current) return;
    
    setLoading(true);
    try {
      await Promise.all([onRefreshRef.current(), loadChatOps()]);
      toast.success('История обновлена');
    } catch (error) {
      toast.error('Ошибка при обновлении истории');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const handleOpenModal = (imageUrl) => {
    setModalImage(imageUrl);
  };

  const handleCloseModal = () => {
    setModalImage(null);
  };

  // Флаг для предотвращения множественных одновременных запросов
  const isLoadingRef = React.useRef(false);
  
  // Обработчик скролла для загрузки следующих записей
  useEffect(() => {
    if (!hasMore || !onLoadMore) {
      return; // Нет больше записей
    }

    let ticking = false;
    let lastScrollTop = 0;
    let scrollTimeout = null;
    
    const handleScroll = () => {
      // Дебаунсинг - обрабатываем скролл не чаще чем раз в 100ms
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      scrollTimeout = setTimeout(() => {
        if (!ticking && !isLoadingRef.current && !loadingMore) {
          window.requestAnimationFrame(() => {
            // Проверяем, дошли ли до конца страницы
            const currentScrollTop = window.scrollY || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            
            // Загружаем еще, когда до конца осталось 500px
            // И только если скроллим вниз (не вверх)
            if (currentScrollTop > lastScrollTop && 
                documentHeight - currentScrollTop - windowHeight < 500) {
              if (!isLoadingRef.current && hasMore && !loadingMore) {
                handleLoadMore();
              }
            }
            
            lastScrollTop = currentScrollTop;
            ticking = false;
          });
          ticking = true;
        }
      }, 100);
    };

    // Добавляем обработчик с опцией passive для лучшей производительности
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
    // handleLoadMore и loadingMore используются внутри через refs и проверки
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, onLoadMore]);

  // Функция загрузки следующих записей
  const handleLoadMore = React.useCallback(async () => {
    // Защита от множественных одновременных запросов
    if (isLoadingRef.current || loadingMore || !hasMore || !onLoadMore) {
      return;
    }

    isLoadingRef.current = true;
    setLoadingMore(true);
    
    try {
      const currentJobsLength = jobsRef.current.length;
      const newJobs = await onLoadMore(currentJobsLength, 20, true);
      
      // Если загрузили меньше записей, чем запрашивали, значит больше нет
      if (!newJobs || newJobs.length < 20) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more jobs:', error);
      toast.error('Ошибка при загрузке записей');
      setHasMore(false); // Останавливаем загрузку при ошибке
    } finally {
      setLoadingMore(false);
      // Задержка перед сбросом флага, чтобы предотвратить повторные запросы
      setTimeout(() => {
        isLoadingRef.current = false;
      }, 500);
    }
  }, [loadingMore, hasMore, onLoadMore]);

  // Обновляем ref при изменении jobs
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  // Обновление списка при заходе на страницу истории
  // Всегда обновляем при переходе на /history (включая повторный заход после других разделов)
  const onRefreshRef = React.useRef(onRefresh);
  
  // Обновляем ref при изменении onRefresh
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);
  
  useEffect(() => {
    if (!onRefreshRef.current || location.pathname !== '/history') {
      return;
    }
    setHasMore(true);
    onRefreshRef.current();
  }, [location.pathname]);

  // Автообновление только статусов активных генераций (без полной перезагрузки)
  useEffect(() => {
    let intervalId = null;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    const updateActiveJobStatuses = async () => {
      if (isUpdatingRef.current) return;
      
      // Получаем список активных jobs (с учетом обновлений статусов)
      const currentJobs = jobsRef.current.map(job => {
        const update = statusUpdatesRef.current[job.public_id];
        return update ? { ...job, ...update } : job;
      });
      
      const activeJobs = currentJobs.filter(job => 
        job.status === 'processing' || job.status === 'pending'
      );
      
      if (activeJobs.length === 0) {
        return; // Нет активных jobs, не обновляем
      }
      
      isUpdatingRef.current = true;
      try {
        // Получаем только обновления статусов
        const publicIds = activeJobs.map(job => job.public_id).join(',');
        const response = await axios.get('/api/services/jobs/status-updates', {
          params: { public_ids: publicIds }
        });
        
        if (response.data && Array.isArray(response.data)) {
          // Сохраняем обновления в локальное хранилище и показываем уведомления
          response.data.forEach(update => {
            const previousUpdate = statusUpdatesRef.current[update.public_id];
            const previousStatus = previousUpdate?.status;
            const currentStatus = update.status;
            
            // Показываем уведомление при изменении статуса
            if (previousStatus && previousStatus !== currentStatus) {
              if (currentStatus === 'completed') {
                // Воспроизводим звук успешной генерации
                playSuccessSound();
                
                toast.success('Генерация завершена!', {
                  icon: '✅',
                  duration: 4000
                });
              } else if (currentStatus === 'failed') {
                const errorMsg = update.error_message || 'Ошибка генерации';
                // Используем translate_error_from_technical если доступен
                toast.error(`Генерация завершилась с ошибкой: ${errorMsg.substring(0, 100)}`, {
                  icon: '❌',
                  duration: 6000
                });
              }
            }
            
            statusUpdatesRef.current[update.public_id] = {
              status: update.status,
              output_image_url: update.output_image_url,
              error_message: update.error_message,
              completed_at: update.completed_at
            };
          });
          
          // Триггерим перерисовку для применения обновлений
          setStatusUpdateTrigger(prev => prev + 1);
          
          retryCount = 0;
        }
      } catch (error) {
        console.error('Error updating job statuses:', error);
        retryCount++;
        
        // При ошибке увеличиваем интервал
        if (error.response?.status === 502 || retryCount >= MAX_RETRIES) {
          if (intervalId) {
            clearInterval(intervalId);
          }
          // Увеличиваем интервал до 30 секунд при ошибках
          scheduleStatusUpdate(30000);
          retryCount = 0;
        }
      } finally {
        isUpdatingRef.current = false;
      }
    };

    const scheduleStatusUpdate = (delay) => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      
      intervalId = setInterval(() => {
        updateActiveJobStatuses();
      }, delay);
    };

    // Начинаем с интервала 5 секунд для более быстрого обновления
    // Периодическая задача на бэкенде обновляет БД каждую минуту,
    // поэтому пользователь увидит обновление максимум через 1 минуту + 5 секунд
    scheduleStatusUpdate(5000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs.length]); // Зависим только от количества jobs

  const resolveJobDetailUrl = (job) => {
    if (!job?.public_id) return null;
    if (job.preset_id) {
      return `/presets/${encodeURIComponent(job.preset_id)}?id=${encodeURIComponent(job.public_id)}&from=history`;
    }
    if (job.ai_model?.slug) {
      return `/models/${encodeURIComponent(job.ai_model.slug)}?id=${encodeURIComponent(job.public_id)}&from=history`;
    }
    return null;
  };

  const handleJobClick = (job, e) => {
    if (e && e.target.closest('.download-btn')) {
      return;
    }
    const url = resolveJobDetailUrl(job);
    if (!url) return;

    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(url, '_blank');
      return;
    }

    navigate(url);
  };

  const handleJobMouseDown = (job, e) => {
    if (e.button !== 1) return;
    const url = resolveJobDetailUrl(job);
    if (!url) return;
    e.preventDefault();
    window.open(url, '_blank');
  };

  return (
    <div className="job-history">
      <div className="job-history-header">
        <h2>История генераций</h2>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={loading ? 'animate-spin' : ''} size={16} />
            Обновить
          </button>
        </div>
      </div>

      {/* Фильтры по статусам */}
      <div className="status-filters">
        <button
          className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          Все <span className="count">{statusCounts.all}</span>
        </button>
        <button
          className={`filter-btn ${statusFilter === 'completed' ? 'active' : ''}`}
          onClick={() => setStatusFilter('completed')}
        >
          <CheckCircle size={16} />
          Завершено <span className="count">{statusCounts.completed}</span>
        </button>
        <button
          className={`filter-btn ${statusFilter === 'processing' ? 'active' : ''}`}
          onClick={() => setStatusFilter('processing')}
        >
          <Loader size={16} />
          Обработка <span className="count">{statusCounts.processing}</span>
        </button>
        <button
          className={`filter-btn ${statusFilter === 'failed' ? 'active' : ''}`}
          onClick={() => setStatusFilter('failed')}
        >
          <XCircle size={16} />
          Ошибка <span className="count">{statusCounts.failed}</span>
        </button>
      </div>

      {(initialLoading || (loading && jobs.length === 0)) ? ( // Показываем loading если идет загрузка или нет данных и идет загрузка
        <div className="loading-state">
          <Loader className="animate-spin" size={48} />
          <p>Загрузка истории генераций...</p>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="empty-state">
          <Clock size={48} />
          <p>
            {statusFilter === 'all' 
              ? 'У вас пока нет операций' 
              : `Нет операций со статусом "${getStatusText(statusFilter)}"`}
          </p>
        </div>
      ) : (
        <div className="jobs-table-container">
          <table className="jobs-table">
            <thead>
              <tr>
                <th>Результат</th>
                <th>Статус</th>
                <th>Время</th>
                <th>Id</th>
                <th>Название</th>
                <th>Стоимость</th>
                <th>Продолжительность</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((job) => (
                <tr 
                  key={job.id} 
                  className="job-row"
                  onClick={(e) => {
                    if (job.kind === 'chat') return;
                    if (!e.target.closest('.download-btn') && resolveJobDetailUrl(job)) {
                      handleJobClick(job, e);
                    }
                  }}
                  onMouseDown={(e) => {
                    if (job.kind === 'chat') return;
                    if (!e.target.closest('.download-btn') && resolveJobDetailUrl(job)) {
                      handleJobMouseDown(job, e);
                    }
                  }}
                  style={{ cursor: job.kind === 'chat' ? 'default' : (resolveJobDetailUrl(job) ? 'pointer' : 'default') }}
                >
                  <td className="result-cell">
                    {job.kind === 'chat' ? (
                      <div className="job-result-thumb">
                        <div className="audio-history-thumb" title="Чат">
                          <MessageSquare size={22} />
                        </div>
                      </div>
                    ) : (() => {
                      const outputUrl = normalizeStaticUrl(job.output_file_url || job.output_image_url);
                      const isValidOutput = outputUrl &&
                                          outputUrl !== 'None' &&
                                          outputUrl !== 'null' &&
                                          outputUrl.trim() !== '';
                      return isValidOutput ? (
                      <div 
                        className="job-result-thumb"
                        onClick={(e) => {
                          e.stopPropagation();
                          const outputUrl = job.output_file_url || job.output_image_url;
                          handleOpenModal(outputUrl);
                        }}
                      >
                        {(() => {
                          const outputUrl = normalizeStaticUrl(job.output_file_url || job.output_image_url);
                          return isAudioFile(outputUrl) ? (
                            <div className="audio-history-thumb" title="Аудио">
                              <Music size={22} />
                            </div>
                          ) : isVideoFile(outputUrl) ? (
                            <>
                              <img 
                                src={getThumbnailUrl(outputUrl) || outputUrl}
                                alt="Video thumbnail"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                loading="lazy"
                                onError={(e) => {
                                  // Если миниатюра не загрузилась, используем оригинал
                                  if (e.target.src !== outputUrl) {
                                    e.target.src = outputUrl;
                                  }
                                }}
                              />
                              <div className="video-play-indicator">
                                <Play size={20} fill="white" />
                              </div>
                            </>
                          ) : (
                            <img 
                              src={getThumbnailUrl(outputUrl) || outputUrl}
                              alt="Result"
                              loading="lazy"
                              onError={(e) => {
                                // Если миниатюра не загрузилась, используем оригинал
                                if (e.target.src !== outputUrl) {
                                  e.target.src = outputUrl;
                                }
                              }}
                            />
                          );
                        })()}
                      </div>
                    ) : (
                      <span className="no-result">—</span>
                    );
                    })()}
                  </td>
                  <td className="status-cell">
                    <div className={`job-status status-${job.status}`}>
                      {getStatusIcon(job.status)}
                      <span className="status-text">{getStatusText(job.status)}</span>
                    </div>
                  </td>
                  <td className="time-cell">
                    {formatDate(job.created_at)}
                  </td>
                  <td className="id-cell">
                    <code>{job.public_id || job.id}</code>
                  </td>
                  <td className="model-cell">
                    {job.kind === 'chat' ? (
                      <div className="model-info">
                        <span className="model-name">Чат · {job.model_name}</span>
                        {job.title && <span className="model-author">{job.title}</span>}
                      </div>
                    ) : job.preset_id ? (
                      presets[job.preset_id] ? (
                        <div className="model-info">
                          <span className="model-name">{presets[job.preset_id].title}</span>
                        </div>
                      ) : (
                        <div className="model-info">
                          <span className="model-name">{job.ai_model?.name || 'Эффект'}</span>
                        </div>
                      )
                    ) : job.ai_model ? (
                      <div className="model-info">
                        <span className="model-name">{job.ai_model.name}</span>
                        {job.ai_model.author && (
                          <span className="model-author">by {job.ai_model.author}</span>
                        )}
                      </div>
                    ) : (
                      <span className="no-model">—</span>
                    )}
                  </td>
                  <td className="cost-cell">
                    <span
                      className="job-cost"
                      title={job.kind === 'chat'
                        ? `Вход ${job.prompt_tokens || 0} · ответ ${job.completion_tokens || 0}`
                        : undefined}
                    >
                      <Zap size={12} />
                      {job.kind === 'chat'
                        ? formatChatTokens(job.billed_tokens)
                        : (job.status === 'failed' ? 0 : (job.bonuses_spent || job.ai_model?.cost_in_bonuses || 0))}
                    </span>
                  </td>
                  <td className="duration-cell">
                    {job.processing_time_seconds ? `${job.processing_time_seconds}с` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loadingMore && (
            <div className="loading-more">
              <Loader className="animate-spin" size={20} />
              <span>Загрузка...</span>
            </div>
          )}
          {!hasMore && filteredJobs.length > 0 && (
            <div className="no-more-jobs">
              <p>Все записи загружены</p>
            </div>
          )}
        </div>
      )}
      
      <ImageModal 
        imageUrl={modalImage}
        onClose={handleCloseModal} 
      />
    </div>
  );
}

export default JobHistory;
